import { Fragment, ReactNode } from 'react';
import moment from 'moment';

// Aufteilung der Vertriebsdirektionen in Vertriebsgebiete (wie im Quartalsbericht)
export const VG_NORD = ['Berlin', 'Hamburg', 'Köln', 'Leipzig'];
export const VG_SUED = ['Frankfurt', 'München', 'Nürnberg', 'Stuttgart'];

export const standDatum = () => moment().format('DD.MM.YYYY');

export enum UsageSource {
	dvs = 'Quelle: DVS Datenbank',
	matomo = 'Quelle: Matomo Analytics',
}

// Deckblatt des Quartalsberichts
export const UsageTitleSlide = () => {
	return (
		<div className="usage-slide is-title-slide page-break-after page-break-avoid">
			<div className="title-accent" />
			<h1 className="title-main">DVS – Digitale Vertriebssteuerung</h1>
			<div className="title-sub">{`Quartalsbericht Q${moment().quarter()}/${moment().year()}`}</div>
			<div className="title-stand">Stand: {standDatum()}</div>
		</div>
	);
};

interface IUsageSlideProps {
	title: string;
	source: UsageSource;
	classModifier?: string;
	children: ReactNode;
}

// Eine "Folie" des Nutzungsberichts: Titel, Stand, Inhalt, Quellenangabe.
// Format ist so gehalten, dass die Karte 1:1 in eine PPT übernommen werden kann.
export const UsageSlide = (props: IUsageSlideProps) => {
	return (
		<div className={`usage-slide page-break-after page-break-avoid ${props.classModifier ?? ''}`}>
			<div className="slide-header">
				<div className="slide-title">{props.title}</div>
			</div>
			<div className="slide-content">{props.children}</div>
			<div className="slide-source">{props.source}</div>
		</div>
	);
};

export interface IUsageCompareRow {
	label: string;
	curr: number;
	// ohne prev entfällt der blasse Vorjahresbalken
	prev?: number;
	// vorformatierter Wert; sonst formatValue(curr)
	valueText?: string;
	// vorformatiertes Delta; dir steuert die Chip-Farbe
	delta?: { text: string; dir: 'up' | 'down' | 'flat' };
}

interface IUsageCompareRowsProps {
	rows: IUsageCompareRow[];
	// Legende erscheint nur bei zwei Serien (curr + prev)
	currLabel?: string;
	prevLabel?: string;
	// Skalenmaximum, default 100 (Prozentwerte)
	max?: number;
	formatValue?: (v: number) => string;
	// Prozent-Ticks 0/25/.../100 an der Achse (nur bei Prozentskala sinnvoll)
	showTicks?: boolean;
	// vertikale Referenzlinie über alle Zeilen (z.B. Ø ABV)
	avg?: { value: number; label: string };
	// Summenzeile unten rechts unter der Wertespalte (z.B. Σ ABV)
	summary?: { label: string; value: string };
}

const AXIS_TICKS = [0, 25, 50, 75, 100];

// Horizontale Vergleichszeilen: blasser Vorjahresbalken hinter dem schmaleren
// Balken des aktuellen Jahres, rechts Wert und Delta-Chip.
// Bewusst reines HTML/CSS (kein ECharts), damit die Folie ruhig und PPT-tauglich bleibt.
export const UsageCompareRows = (props: IUsageCompareRowsProps) => {
	const { rows, currLabel, prevLabel, avg, showTicks, summary } = props;
	const max = props.max ?? 100;
	const formatValue = props.formatValue ?? ((v: number) => `${v}%`);
	const pct = (v: number) => (max > 0 ? (v / max) * 100 : 0);
	const hasDelta = rows.some((r) => r.delta);
	const showLegend = !!(currLabel && prevLabel);

	return (
		<div className="usage-compare-rows">
			{showLegend && (
				<div className="compare-legend">
					<span>
						<i className="legend-swatch is-curr" /> {currLabel}
					</span>
					<span>
						<i className="legend-swatch is-prev" /> {prevLabel}
					</span>
				</div>
			)}
			<div className={`compare-grid ${hasDelta ? '' : 'is-no-delta'}`}>
				{rows.map((r) => (
					<Fragment key={r.label}>
						<div className="row-label">{r.label}</div>
						<div className="row-track">
							{r.prev !== undefined && <div className="bar-prev" style={{ width: `${pct(r.prev)}%` }} />}
							<div className="bar-curr" style={{ width: `${pct(r.curr)}%` }} />
							{avg && <div className="avg-line" style={{ left: `${pct(avg.value)}%` }} />}
						</div>
						<div className="row-value">{r.valueText ?? formatValue(r.curr)}</div>
						{hasDelta && (r.delta ? <div className={`row-delta is-${r.delta.dir}`}>{r.delta.text}</div> : <div />)}
					</Fragment>
				))}
				{(showTicks || avg) && (
					<>
						<div />
						<div className="row-axis">
							{showTicks &&
								AXIS_TICKS.map((t) => (
									<span key={t} className={`axis-tick ${t === 0 ? 'is-first' : ''} ${t === 100 ? 'is-last' : ''}`} style={{ left: `${t}%` }}>
										{t}%
									</span>
								))}
							{avg && (
								<span className="axis-avg" style={{ left: `${pct(avg.value)}%` }}>
									{avg.label}
								</span>
							)}
						</div>
						<div />
						{hasDelta && <div />}
					</>
				)}
				{summary && (
					<div className="row-summary">
						<span>
							{summary.label} <strong>{summary.value}</strong>
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

interface IUsageMultiRowsProps {
	categories: string[];
	// letzte Serie = aktuelles Jahr (volle Akzentfarbe), frühere Jahre werden zunehmend blasser
	series: { name: string; data: number[] }[];
	formatValue: (v: number) => string;
}

// Mehrere Serien (Jahre) je Kategorie als dünne, untereinander liegende Balken —
// gleiche Optik wie UsageCompareRows, für die Entwicklungs-Folien.
export const UsageMultiRows = (props: IUsageMultiRowsProps) => {
	const { categories, series, formatValue } = props;
	const max = Math.max(1, ...series.flatMap((s) => s.data));
	// Abstand der Serie zum aktuellen Jahr bestimmt die Blässe (0 = aktuelles Jahr)
	const backIdx = (seriesIdx: number) => Math.min(series.length - 1 - seriesIdx, 4);

	return (
		<div className="usage-multi-rows">
			{series.length > 1 && (
				<div className="compare-legend">
					{series.map((s, idx) => (
						<span key={s.name}>
							<i className={`legend-swatch is-back-${backIdx(idx)}`} /> {s.name}
						</span>
					))}
				</div>
			)}
			<div className="multi-grid">
				{categories.map((cat, catIdx) => (
					<Fragment key={cat}>
						<div className="row-label">{cat}</div>
						<div className="row-bars">
							{series.map((s, idx) => (
								<div key={s.name} className="bar-line">
									{/* 86% statt 100%, damit rechts Platz für das Wertelabel bleibt */}
									<div className={`bar is-back-${backIdx(idx)}`} style={{ width: `${((s.data[catIdx] || 0) / max) * 86}%` }} />
									<span className="bar-value">{formatValue(s.data[catIdx] || 0)}</span>
								</div>
							))}
						</div>
					</Fragment>
				))}
			</div>
		</div>
	);
};
